/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { CollectionsModule, toLayoutValue } from '@coursera-web/cds-labs';
import type { Filter } from '@coursera-web/cds-labs';

import useCollection from 'bundles/campaigns/hooks/useCollection';

type ImageType = React.ComponentProps<typeof CollectionsModule>['backgroundImage'];

type Props = {
  headline: string;
  description: string;
  ctaText: string;
  ctaTarget: string;
  onClick?: () => void;
  showCtaArrowIcon?: boolean;
  collectionSets: {
    id: string;
    collectionModuleId: string;
    presetCollectionId: string;
    filterName?: string;
  }[];
  numberOfEntriesPerCollection?: number;
  backgroundImage?: ImageType;
  layout: string;
};

const styles = {
  container: css`
    margin: var(--cds-spacing-400) 0;
    border-radius: var(--cds-border-radius-200);
    overflow: hidden;
  `,
};

const SpotlightCollections = ({
  headline,
  description,
  ctaText,
  ctaTarget,
  onClick,
  showCtaArrowIcon,
  collectionSets,
  numberOfEntriesPerCollection,
  backgroundImage,
  layout,
}: Props) => {
  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = React.useState(0);
  const handleFilterChange = (index: number) => {
    setCurrentCollectionSetIndex(index);
  };

  const filters = collectionSets.map((collectionSet, index) => {
    return {
      id: index.toString(),
      filterName: collectionSet.filterName,
    } as Filter;
  });

  const { collection, loading: isCollectionsLoading } = useCollection({
    queryCollectionId: collectionSets[currentCollectionSetIndex].collectionModuleId,
    additionalInput: {
      preset_collection_id: collectionSets[currentCollectionSetIndex].presetCollectionId,
    },
  });

  return (
    <div css={styles.container}>
      <CollectionsModule
        filters={filters.length > 0 ? filters : null}
        onFilterChange={handleFilterChange}
        currentCollectionSetIndex={currentCollectionSetIndex}
        collections={collection ? [collection] : undefined}
        numOfEntriesPerCollection={numberOfEntriesPerCollection || 3}
        isCollectionsLoading={isCollectionsLoading}
        showAllEntries
        lazyLoadImage
        description={description}
        headline={headline}
        layout={toLayoutValue(layout)}
        backgroundColor="light-blue"
        showRoundedCorners
        isPageGridContainerAppliedOnPage={true}
        backgroundImage={backgroundImage}
        cta={{
          target: ctaTarget,
          text: ctaText,
          variant: 'primary',
          showCtaArrowIcon,
          onClick,
        }}
      />
    </div>
  );
};

export default SpotlightCollections;
